import {
  Chip,
  IconButton,
  Button,
  Tab,
  Modal,
  Paper,
  Typography,
} from '@material-ui/core';
import Box from '@mui/material/Box';
import { DataGrid } from '@mui/x-data-grid';
import { styled } from '@material-ui/core';
import { Palette } from '@material-ui/core/styles/createPalette';

export const getStatusColor = (
  status: 'new' | 'approved' | 'declined',
  palette: Palette,
) => {
  const colorByStatus: Record<'new' | 'approved' | 'declined', string> = {
    new: palette.warning.main,
    approved: palette.success.main,
    declined: palette.error.main,
  };
  return colorByStatus[status];
};

const basePillButton = {
  borderRadius: 24,
  padding: '4px 12px',
  fontSize: 10,
  fontWeight: 700,
};

export const ApproveButton = styled(Button)(({ theme }) => ({
  ...basePillButton,
  borderColor: theme.palette.primary.main,
  color: theme.palette.primary.main,
  marginRight: 8,
}));

export const DeclineButton = styled(Button)(({ theme }) => ({
  ...basePillButton,
  borderColor: theme.palette.error.main,
  color: theme.palette.error.main,
}));

export const StyledDataGrid = styled(DataGrid)({
  overflowX: 'hidden',
  '& .MuiDataGrid-virtualScroller': {
    overflowX: 'hidden',
  },
  '& .MuiDataGrid-row.clickable': {
    cursor: 'pointer',
  },
  '& .MuiDataGrid-cell.actions-cell': {
    display: 'flex',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 700,
  },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
    outline: 'none',
    '& .MuiDataGrid-columnSeparator': {
      opacity: 1,
    },
  },
});

export const ModalContainer = styled(Modal)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const StyledPaper = styled(Paper)({
  width: 560,
  position: 'absolute',
  margin: 'auto',
  padding: '24px',
  borderRadius: 6,
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  boxShadow: '0px 12px 16px 0px rgba(0, 0, 0, 0.58)',
});

export const StyledChip = styled(Chip)(({ theme }) => ({
  height: 12,
  minWidth: 16,
  backgroundColor: `${theme.palette.error.main} !important`,
  marginLeft: 8,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  verticalAlign: 'middle',
  '& .MuiChip-label': {
    paddingLeft: 6,
    paddingRight: 6,
    fontWeight: 700,
    fontSize: 8,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    overflow: 'visible',
    textOverflow: 'clip',
  },
}));

export const StyledTab = styled(Tab)({
  fontWeight: 700,
  minWidth: 0,
  padding: '12px 16px',
  fontFamily: 'Red Hat Display',
  ['&:not(&.Mui-selected)']: {
    color: 'rgba(9, 34, 65, 0.7)',
  },
});

export const SortButton = styled(IconButton)({
  padding: 0,
});

export const IconButtonWrapper = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
});

export const ToolbarBox = styled(Box)({
  width: '100%',
  padding: '0 16px',
  display: 'flex',
  justifyContent: 'space-between',
});

export const SectionDivider = styled('div')(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  margin: '20px 0',
}));

export const SummaryItemContainer = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 16,
});

export const SummaryIconWrapper = styled('div')(({ theme }) => ({
  marginTop: 2,
  color: theme.palette.text.secondary,
}));

export const StatusPill = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 12px',
  borderRadius: 24,
  backgroundColor: theme.palette.common.white,
  border: `1px solid ${theme.palette.divider}`,
}));

export const FieldLabelText = styled(Typography)({
  fontWeight: 700,
  fontSize: 14,
});

export const StatusText = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: 12,
  fontFamily: 'Red Hat Display',
  color: theme.palette.text.primary,
}));

export const ActivityCard = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8,
  padding: 16,
  height: '100%',
}));

export const SuccessPill = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '8px 16px',
  borderRadius: 24,
  backgroundColor: theme.palette.success.main,
}));

export const InverseStatusText = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: 12,
  fontFamily: 'Red Hat Display',
  color: theme.palette.common.white,
}));

export const HeaderBar = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '20px 0',
});

export const AttachmentRowContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: 12,
});

export const AttachmentItemContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '50%',
});

export const SectionTitle = styled(Typography)({
  fontSize: 20,
  fontWeight: 700,
  fontFamily: 'Red Hat Display',
  marginBottom: 24,
});

export const ContentWrapper = styled('div')(() => ({
  '& [class*="BackstageContent-root"]': {
    paddingBottom: '0 !important',
  },
}));
